import React, { useRef } from "react";
import { PaymentDetail } from "@features/cx/payments/types";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import {
  usePaymentMode,
} from "../../../../hooks";
import {
  CheckCircle2,
  Printer,
  Mail,
  Download,
  Check,
} from "lucide-react";
import { toast } from "@shared/hooks/use-toast";
import { Button } from "@shared/ui/button";
import { normalizeDate } from "@shared/utils/dateUtils";

interface ConfirmationStepProps {
  payment: PaymentDetail;
  refundData: any;
  refundId: string | null;
  onClose: () => void;
}

export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({
  payment,
  refundData,
  refundId,
  onClose,
}) => {
  const currency = useCurrency();
  const remoteUtilityId = useRemoteUtilityId();

  const { data: paymentModesData } = usePaymentMode(Number(remoteUtilityId));

  const paymentModeList = Array.isArray(paymentModesData)
    ? paymentModesData
    : Array.isArray(paymentModesData?.result)
      ? paymentModesData.result
      : [];

  const selectedMode = paymentModeList.find(
    (m: any) => m.name === refundData?.refundMethod,
  );
  const customFields = selectedMode?.customFields || [];

  const isCheque = refundData?.refundMethod === "Cheque";
  const amountStr = `${currency} ${refundData?.finalAmount?.toFixed(2)}`;
  const consumerName = `${payment.consumerFirstName} ${payment.consumerLastName}`;
  const receiptRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const receiptContent = receiptRef.current;
    if (!receiptContent) return;

    const printWindow = window.open("", "_blank", "width=800,height=600");
    if (printWindow) {
      const styles = Array.from(document.styleSheets)
        .map((styleSheet) => {
          try {
            return Array.from(styleSheet.cssRules)
              .map((rule) => rule.cssText)
              .join("\n");
          } catch (e) {
            return "";
          }
        })
        .join("\n");

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <meta charset="UTF-8">
            <meta name="viewport" content="width=device-width, initial-scale=1.0">
            <title>Refund Receipt ${refundId || ""}</title>
            <style>
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif; padding: 20px; background: white; color: #000; }
              @media print { body { padding: 0; } @page { margin: 1cm; size: A4; } }
              ${styles}
            </style>
          </head>
          <body>
            ${receiptContent.innerHTML}
            <script>
              window.onload = function() {
                window.print();
              };
            </script>
          </body>
        </html>
      `);
      printWindow.document.close();
      toast({ description: "Refund receipt sent to printer" });
    }
  };

  const handleDownload = async () => {
    if (!receiptRef.current) return;
    try {
      const html2pdf = (await import("html2pdf.js")).default;
      const options = {
        margin: 10,
        filename: `refund-receipt-${refundId || Date.now()}.pdf`,
        image: { type: "jpeg" as const, quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: {
          unit: "mm" as const,
          format: "a4",
          orientation: "portrait" as const,
        },
      };
      await html2pdf().set(options).from(receiptRef.current).save();
      toast({ description: "Refund receipt downloaded" });
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      toast({
        title: "Error",
        description: "Failed to generate PDF",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-8 flex flex-col items-center pt-4 pb-2">
      <div
        ref={receiptRef}
        className="w-full space-y-8 flex flex-col items-center"
      >
        <div className="flex flex-col items-center text-center space-y-3">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-1">
            <CheckCircle2 className="w-10 h-10 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-slate-900">
            Refund Processed Successfully
          </h3>
          <p className="text-slate-500 max-w-md text-sm">
            {isCheque
              ? `Refund of ${amountStr} recorded for ${consumerName}. Cheque is pending clearance.`
              : `Refund of ${amountStr} recorded for ${consumerName}. Funds will be released shortly.`}
          </p>
          <div className="bg-green-100/60 border border-green-200 text-green-700 px-4 py-1.5 rounded-md font-medium flex items-center gap-2 text-sm mt-3">
            <Check className="w-4 h-4" /> Refund ID: {refundId}
          </div>
        </div>

        <div className="w-full border border-slate-200 rounded-lg p-6 bg-slate-50/50">
          <div className="flex flex-col w-full">
            <ValueLabel
              layout="horizontal"
              className="w-full justify-between py-3 border-b border-dashed border-slate-200"
              label="Refund ID"
              value={refundId}
            />
            <ValueLabel
              layout="horizontal"
              className="w-full justify-between py-3 border-b border-dashed border-slate-200"
              label="Consumer"
              value={`${consumerName} · ${payment.consumerNo || payment.consumerId}`}
            />
            <ValueLabel
              layout="horizontal"
              className="w-full justify-between py-3 border-b border-dashed border-slate-200"
              label="Original Receipt"
              value={payment.receiptNo || "N/A"}
            />
            <ValueLabel
              layout="horizontal"
              className="w-full justify-between py-3 border-b border-dashed border-slate-200"
              label="Amount"
              value={
                <span className="text-red-600 font-bold">{amountStr}</span>
              }
            />
            {refundData?.disposition === "Refund to Customer" && (
              <>
                <ValueLabel
                  layout="horizontal"
                  className="w-full justify-between py-3 border-b border-dashed border-slate-200"
                  label="Method"
                  value={refundData?.refundMethod}
                />
                {customFields.map((cf: any) => {
                  const fieldName = cf.key || cf.id?.toString();
                  const value = refundData?.[fieldName];
                  if (!value) return null;

                  return (
                    <ValueLabel
                      key={fieldName}
                      layout="horizontal"
                      className="w-full justify-between py-3 border-b border-dashed border-slate-200"
                      label={cf.label}
                      value={
                        value instanceof Date
                          ? value.toLocaleDateString()
                          : typeof value === "object" && value?.startDate
                            ? new Date(value.startDate).toLocaleDateString()
                            : String(value)
                      }
                    />
                  );
                })}
              </>
            )}
            <ValueLabel
              layout="horizontal"
              className="w-full justify-between py-3 border-b border-dashed border-slate-200"
              label="Approver"
              value={refundData?.approver || "—"}
            />
            <ValueLabel
              layout="horizontal"
              className="w-full justify-between py-3 border-slate-200 last:border-0"
              label="Processed At"
              value={normalizeDate(Date())}
            />
          </div>
        </div>
      </div>

      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4">
        <Button variant="outline" onClick={handlePrint}>
          <Printer className="w-4 h-4" /> Print Receipt
        </Button>
        <Button variant="outline" onClick={handleDownload}>
          <Download className="w-4 h-4" /> Download PDF
        </Button>
      </div>

      <div className="w-full pt-6 border-t border-slate-200 flex justify-end gap-3">
        <Button variant="outline" onClick={onClose}>
          Close
        </Button>
        <Button
          variant="default"
          onClick={() => {
            toast({
              title: `Refund ${refundId || ""} issued for ${amountStr}.`,
              variant: "default",
            });
            onClose();
          }}
        >
          Back to Payments
        </Button>
      </div>
    </div>
  );
};
